
import React, { useState, useMemo, useEffect } from 'react';
import { UserProfile, Transaction } from '../types';
import ThemeToggle from './ThemeToggle';
import ConfirmDialog from './ConfirmDialog';
import { googleAuth } from '../utils/googleAuth';
import { supabase } from '../supabase';

interface ProfileProps {
  user: UserProfile | null;
  onLogout: () => void;
  onRefreshData: () => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  onSyncFromSheet: () => void;
  onRepairData?: () => Promise<void>;
  onClearTransactions?: (start: Date, end: Date) => Promise<void>;
  onTriggerVerify: (title: string, message: string, callback: () => void, icon?: string) => void;
  transactions: Transaction[];
  subscriptionStatus?: string;
}

const Profile: React.FC<ProfileProps> = ({ 
  user, 
  onLogout, 
  theme, 
  toggleTheme, 
  onSyncFromSheet, 
  onRepairData,
  onClearTransactions,
  onTriggerVerify,
  transactions,
  subscriptionStatus = 'trial'
}) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [isMaintenanceOpen, setIsMaintenanceOpen] = useState(false);
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  
  const [showEmail, setShowEmail] = useState(true);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('equilibra_privacy_email');
      if (saved !== null) {
        setShowEmail(saved === 'true');
      }
    } catch (e) {}
  }, []);

  const toggleEmailPrivacy = () => {
    setShowEmail(prev => {
      const newVal = !prev;
      try {
        localStorage.setItem('equilibra_privacy_email', String(newVal));
      } catch (e) {}
      return newVal;
    });
  };

  const isAnnual = subscriptionStatus === 'annual';
  const isTrial = subscriptionStatus === 'trial';
  const isExpired = subscriptionStatus === 'expired';

  const { canRenew, renewAvailableDate } = useMemo(() => {
    if (isTrial || isExpired) return { canRenew: true, renewAvailableDate: null };
    
    if (isAnnual && user?.subscription_expiry) {
      const expiry = new Date(user.subscription_expiry);
      const now = new Date();
      const oneDayInMs = 24 * 60 * 60 * 1000;
      
      const isWithinWindow = (expiry.getTime() - now.getTime()) <= oneDayInMs;
      const availableDate = new Date(expiry.getTime() - oneDayInMs);
      
      return { 
        canRenew: isWithinWindow, 
        renewAvailableDate: availableDate 
      };
    }
    
    return { canRenew: true, renewAvailableDate: null };
  }, [isTrial, isExpired, isAnnual, user?.subscription_expiry]);

  const [startMonth, setStartMonth] = useState(new Date().getMonth());
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(new Date().getMonth());
  const [endYear, setEndYear] = useState(new Date().getFullYear());
  
  const [isConfirmClearOpen, setIsConfirmClearOpen] = useState(false);

  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const yearsSet = new Set<number>([currentYear]);
    transactions.forEach(t => {
      if (t.date) {
        const y = parseInt(t.date.split('-')[0]);
        if (y) yearsSet.add(y);
      }
    });
    return Array.from(yearsSet).sort((a, b) => a - b);
  }, [transactions]);

  const handleUpgrade = async () => {
    if (!canRenew || isUpgrading || !user) return;
    setIsUpgrading(true);
    try {
      const { data, error } = await supabase.functions.invoke('mercadopago', {
        body: { 
          action: 'create_checkout',
          user_email: user.email,
          user_id: user.id
        }
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
      else alert("Erro ao gerar link de pagamento.");
    } catch (error) {
      alert("Erro de conexão com o sistema de pagamentos.");
    } finally { setIsUpgrading(false); }
  };

  const handleClearAction = async () => {
    if (!onClearTransactions) return;
    const startDate = new Date(startYear, startMonth, 1, 0, 0, 0);
    const endDate = new Date(endYear, endMonth + 1, 0, 23, 59, 59);
    
    if (startDate > endDate) {
      alert("A data de início não pode ser posterior à de fim.");
      return;
    }

    setIsConfirmClearOpen(false);
    onTriggerVerify(
      'Autorizar Limpeza de Dados',
      'Esta ação irá apagar permanentemente os registros do período selecionado. Confirme com sua Chave Mestra.',
      async () => {
        setIsMaintenanceOpen(false);
        await onClearTransactions(startDate, endDate);
      },
      'fa-broom'
    );
  };

  const getInitials = (name?: string) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  return (
    <div className="max-w-2xl mx-auto flex flex-col animate-in fade-in duration-700 pb-20 px-1">
      <div className="sticky top-0 z-50 bg-slate-100/80 dark:bg-slate-950/80 backdrop-blur-xl px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-4 border-b border-slate-200/50 dark:border-slate-800/50 -mx-4 transition-all md:relative md:bg-transparent md:border-none md:p-0 md:mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg md:text-2xl font-black text-slate-900 dark:text-white heading-display uppercase tracking-tight leading-none">Ajustes</h2>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className="w-1 h-1 rounded-full bg-blue-500 animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.5)]"></div>
              <p className="text-[8px] md:text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em]">Soberania de Dados • Equilibra Finance</p>
            </div>
          </div>
          <ThemeToggle theme={theme} toggleTheme={toggleTheme} className="scale-100" />
        </div>
      </div>

      <div className="flex-1 py-4 space-y-6">
        {subscriptionStatus === 'expired' ? (
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 p-4 rounded-2xl flex items-center gap-3 text-red-600 dark:text-red-400 animate-in slide-in-from-top-4 duration-500 shadow-xl shadow-red-500/5">
            <div className="w-8 h-8 rounded-lg bg-red-500 text-white flex items-center justify-center shrink-0 shadow-lg shadow-red-500/30">
              <i className="fa-solid fa-circle-exclamation text-base"></i>
            </div>
            <p className="text-[9px] font-black uppercase tracking-[0.12em] leading-tight">Sua assinatura expirou. Renove para continuar desfrutando da soberania financeira.</p>
          </div>
        ) : user?.subscription_expiry && (new Date(user.subscription_expiry).getTime() - new Date().getTime()) <= 3 * 24 * 60 * 60 * 1000 && (
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 p-4 rounded-2xl flex items-center gap-3 text-amber-600 dark:text-amber-400 animate-in slide-in-from-top-4 duration-500 shadow-xl shadow-amber-500/5">
            <div className="w-8 h-8 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-lg shadow-amber-500/30">
              <i className="fa-solid fa-clock text-base"></i>
            </div>
            <p className="text-[9px] font-black uppercase tracking-[0.12em] leading-tight">
              {isTrial ? 'Seu período de teste' : 'Sua assinatura'} expira em breve ({new Date(user.subscription_expiry).toLocaleDateString()}).
            </p>
          </div>
        )}

        <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-premium flex flex-col items-center text-center relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>
          
          <div className="relative mb-4">
            <div className="w-20 h-20 p-0.5 rounded-2xl bg-gradient-to-tr from-blue-600 via-indigo-500 to-emerald-400 shadow-xl shadow-blue-500/10 group-hover:scale-105 transition-transform duration-700">
              <div className="w-full h-full bg-slate-950 rounded-[0.85rem] flex items-center justify-center text-white text-2xl font-black heading-display">
                {getInitials(user?.full_name)}
              </div>
            </div>
            <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-emerald-500 border-4 border-white dark:border-slate-900 rounded-lg flex items-center justify-center text-white shadow-lg transform rotate-12 group-hover:rotate-0 transition-transform duration-500">
              <i className="fa-solid fa-shield-check text-[9px]"></i>
            </div>
          </div>
          
          <h1 className="text-xl font-black text-slate-900 dark:text-white tracking-tight uppercase leading-none mb-2.5 heading-display">{user?.full_name || 'Usuário'}</h1>
          
          <div className="flex justify-center relative z-10">
            <button 
              type="button"
              className="flex items-center gap-2 group/email cursor-pointer bg-slate-50 dark:bg-slate-800/50 px-4 py-2 rounded-lg border border-slate-100 dark:border-slate-800 transition-all hover:border-blue-500/30 hover:shadow-md outline-none active:scale-95" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleEmailPrivacy();
              }}
            >
              <p className="text-slate-500 dark:text-slate-400 text-[9px] font-black uppercase tracking-[0.15em] pointer-events-none">
                {showEmail ? user?.email : "••••••••@••••.com"}
              </p>
              <i className={`fa-solid ${showEmail ? 'fa-eye' : 'fa-eye-slash'} text-[9px] text-slate-300 dark:text-slate-600 group-hover/email:text-blue-500 transition-colors pointer-events-none`}></i>
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2 px-2">
            <div className="w-1 h-2.5 bg-blue-600 rounded-full"></div>
            <h3 className="text-[8px] font-black uppercase tracking-[0.15em] text-slate-400">Plano e Assinatura</h3>
          </div>
          
          <div className={`rounded-xl shadow-lg border overflow-hidden transition-all duration-700 ${isSubscriptionOpen ? 'bg-slate-950 border-white/10 ring-4 ring-blue-500/5' : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 shadow-premium'}`}>
            <button onClick={() => setIsSubscriptionOpen(!isSubscriptionOpen)} className="w-full p-3 flex justify-between items-center outline-none group">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-base transition-all duration-700 ${isSubscriptionOpen ? 'bg-white text-blue-600 shadow-xl shadow-white/20 scale-105' : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600'}`}>
                  <i className="fa-solid fa-bolt"></i>
                </div>
                <div className="text-left">
                  <p className={`text-[9px] font-black uppercase tracking-[0.15em] mb-0.5 ${isSubscriptionOpen ? 'text-white' : 'text-slate-800 dark:text-slate-200'}`}>Plano {isAnnual ? 'Anual' : 'Trial'}</p>
                  <p className={`text-[7px] uppercase font-bold tracking-widest ${isSubscriptionOpen ? 'text-white/40' : 'text-slate-400'}`}>
                    {`Expira em ${user?.subscription_expiry ? new Date(user.subscription_expiry).toLocaleDateString() : '...'}`}
                  </p>
                </div>
              </div>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-700 ${isSubscriptionOpen ? 'bg-white/10 text-white rotate-180' : 'bg-slate-50 dark:bg-slate-800 text-slate-300'}`}>
                <i className="fa-solid fa-chevron-down text-[7px]"></i>
              </div>
            </button>

            {isSubscriptionOpen && (
              <div className="px-4 pb-4 animate-in slide-in-from-top-4 duration-700 relative">
                <div className="space-y-4 relative z-10 pt-2">
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <h4 className="text-white text-[9px] font-black uppercase tracking-[0.15em] mb-3">Vantagens da Soberania Financeira</h4>
                    <ul className="space-y-2">
                      {[
                        'Sincronização Cloud Ilimitada',
                        'Suporte Prioritário Telegram',
                        'Gráficos Avançados de Patrimônio',
                        'Relatórios de IA Personalizados'
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-white/60 text-[9px] font-bold uppercase tracking-widest">
                          <div className="w-3.5 h-3.5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                            <i className="fa-solid fa-check text-emerald-400 text-[8px]"></i>
                          </div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button 
                    onClick={handleUpgrade}
                    disabled={!canRenew || isUpgrading}
                    className={`w-full py-3 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] shadow-xl transition-all flex items-center justify-center gap-3 transform hover:-translate-y-1 active:scale-95 ${
                      canRenew 
                        ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-500/20' 
                        : 'bg-white/5 text-white/20 border border-white/5 cursor-not-allowed'
                    }`}
                  >
                    {isUpgrading ? <i className="fa-solid fa-spinner fa-spin"></i> : <><i className="fa-solid fa-bolt"></i> Ativar Plano Anual</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2 px-2">
            <div className="w-1 h-2.5 bg-amber-500 rounded-full"></div>
            <h3 className="text-[8px] font-black uppercase tracking-[0.15em] text-slate-400">Manutenção e Dados</h3>
          </div>

          <div className="space-y-2">
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 shadow-premium overflow-hidden transition-all group">
            <button onClick={() => setIsMaintenanceOpen(!isMaintenanceOpen)} className="w-full p-3 flex justify-between items-center outline-none hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-all">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-600 flex items-center justify-center text-base group-hover:scale-110 transition-transform duration-500">
                  <i className="fa-solid fa-broom"></i>
                </div>
                <div className="text-left">
                  <p className="text-[9px] font-black uppercase text-slate-800 dark:text-slate-200 tracking-[0.12em]">Limpeza de Dados</p>
                  <p className="text-[7px] text-slate-400 uppercase font-bold tracking-widest mt-0.5">Manutenção do Histórico</p>
                </div>
              </div>
              <div className={`w-7 h-7 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center transition-all duration-500 ${isMaintenanceOpen ? 'rotate-180' : ''}`}>
                <i className="fa-solid fa-chevron-down text-slate-300 text-[7px]"></i>
              </div>
            </button>

            {isMaintenanceOpen && (
              <div className="px-4 pb-4 space-y-4 animate-in slide-in-from-top-4 duration-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1">Início do Período</span>
                    <div className="grid grid-cols-2 gap-2">
                      <select value={startMonth} onChange={e => setStartMonth(parseInt(e.target.value))} className="bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg text-[9px] font-black uppercase tracking-widest outline-none border border-slate-100 dark:border-slate-800 dark:text-white cursor-pointer hover:border-blue-500/30 transition-colors appearance-none">
                        {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
                      </select>
                      <select value={startYear} onChange={e => setStartYear(parseInt(e.target.value))} className="bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg text-[9px] font-black uppercase tracking-widest outline-none border border-slate-100 dark:border-slate-800 dark:text-white cursor-pointer hover:border-blue-500/30 transition-colors appearance-none">
                        {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.15em] ml-1">Fim do Período</span>
                    <div className="grid grid-cols-2 gap-2">
                      <select value={endMonth} onChange={e => setEndMonth(parseInt(e.target.value))} className="bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg text-[9px] font-black uppercase tracking-widest outline-none border border-slate-100 dark:border-slate-800 dark:text-white cursor-pointer hover:border-blue-500/30 transition-colors appearance-none">
                        {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
                      </select>
                      <select value={endYear} onChange={e => setEndYear(parseInt(e.target.value))} className="bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg text-[9px] font-black uppercase tracking-widest outline-none border border-slate-100 dark:border-slate-800 dark:text-white cursor-pointer hover:border-blue-500/30 transition-colors appearance-none">
                        {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <button onClick={() => setIsConfirmClearOpen(true)} className="w-full py-3 bg-red-500/10 text-red-600 text-[10px] font-black uppercase tracking-[0.2em] rounded-xl hover:bg-red-600 hover:text-white transition-all shadow-lg shadow-red-500/5 active:scale-95">Executar Limpeza Permanente</button>
              </div>
            )}
          </div>

          {onRepairData && (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 shadow-premium overflow-hidden group">
              <button 
                onClick={() => {
                  onTriggerVerify(
                    'Reparar Planilha',
                    'Deseja reorganizar todas as colunas na sua planilha do Google Sheets para corrigir erros de sincronização? Esta ação requer sua Chave Mestra para re-sincronizar os dados protegidos.',
                    async () => {
                      await onRepairData();
                    },
                    'fa-wrench'
                  );
                }} 
                className="w-full p-3 flex justify-between items-center outline-none hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 flex items-center justify-center text-base group-hover:scale-110 transition-transform duration-500">
                    <i className="fa-solid fa-wrench"></i>
                  </div>
                  <div className="text-left">
                    <p className="text-[9px] font-black uppercase text-slate-800 dark:text-slate-200 tracking-[0.12em]">Reparar Planilha</p>
                    <p className="text-[7px] text-slate-400 uppercase font-bold tracking-widest mt-0.5">Sincronização de Colunas</p>
                  </div>
                </div>
                <div className="w-7 h-7 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center">
                  <i className="fa-solid fa-chevron-right text-slate-300 text-[7px]"></i>
                </div>
              </button>
            </div>
          )}

          {/* Suporte Telegram - v1.2.1 */}
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 shadow-premium overflow-hidden group">
            <button 
              onClick={() => {
                const rawUrl = import.meta.env.VITE_TELEGRAM_SUPPORT_URL || 'https://t.me/equilibrafinance';
                const telegram = rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`;
                window.open(telegram, '_blank');
              }}
              className="w-full p-3 flex justify-between items-center outline-none transition-all hover:bg-slate-50 dark:hover:bg-slate-800/50"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 flex items-center justify-center text-base group-hover:scale-110 transition-transform duration-500">
                  <i className="fa-brands fa-telegram"></i>
                </div>
                <div className="text-left">
                  <p className="text-[9px] font-black uppercase text-slate-800 dark:text-slate-200 tracking-[0.12em]">Suporte Telegram</p>
                  <p className="text-[7px] text-slate-400 uppercase font-bold tracking-widest mt-0.5">
                    Atendimento ao Cliente
                  </p>
                </div>
              </div>
              <div className="w-7 h-7 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center">
                <i className="fa-solid fa-chevron-right text-slate-300 text-[7px]"></i>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2 px-2 pt-1">
            <div className="w-1 h-2.5 bg-red-600 rounded-full"></div>
            <h3 className="text-[8px] font-black uppercase tracking-[0.15em] text-slate-400">Conta e Sessão</h3>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 shadow-premium overflow-hidden group">
            <button 
              onClick={onLogout} 
              className="w-full p-3 flex justify-between items-center outline-none hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 flex items-center justify-center text-base group-hover:scale-110 transition-transform duration-500">
                  <i className="fa-solid fa-power-off"></i>
                </div>
                <div className="text-left">
                  <p className="text-[9px] font-black uppercase text-slate-800 dark:text-slate-200 tracking-[0.12em]">Encerrar Sessão</p>
                  <p className="text-[7px] text-slate-400 uppercase font-bold tracking-widest mt-0.5">Sair da Conta</p>
                </div>
              </div>
              <div className="w-7 h-7 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center">
                <i className="fa-solid fa-chevron-right text-slate-300 text-[7px]"></i>
              </div>
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog 
        isOpen={isConfirmClearOpen} 
        onClose={() => setIsConfirmClearOpen(false)} 
        onConfirm={handleClearAction} 
        title="Apagar Lançamentos?" 
        message={`Deseja confirmar a exclusão permanente dos dados do período selecionado?`} 
        confirmText="Confirmar" 
        type="warning"
      />
    </div>
  );
};

export default Profile;
